/**
 * STARTING THE FIGHT - what crossing into Combat from a scene sets up.
 *
 * Any mode can be switched to any other with one press; this is the fuller
 * door, for when a fight breaks out of a conversation or an infiltration
 * and three things want deciding at once:
 *
 *   WHERE THE SIDES STAND. People added during Narrative or Stealth were
 *   parked on one small ring in the middle of the map, the kill-team and
 *   the sentries shoulder to shoulder. `apart` puts the party around one
 *   point and the opposition around another, a set distance between them.
 *
 *   THE ORDER. Everyone in the fight joins it; initiative is rolled for
 *   whoever has none, or for everybody when asked. An order already running
 *   (Stealth's turns, an Alarm) is KEPT by default - its round, whose turn
 *   it is, every place - and newcomers come in through the door; or the GM
 *   restarts it on round 1 (the table, 2026-10-28).
 *
 *   SURPRISE. One side may sit out the first round (`engine/turnTracker`).
 *
 * This file is the pure half: the plan, who sits out, where two sides
 * apart stand, and the order a fight opens on. The dice (initiative) are
 * rolled by the caller and arrive as data.
 */
import { deploy, defaultRing } from '../battlefield/deployment';
import { clampToBoundary } from '../battlefield/boundary';
import type { Boundary } from '../battlefield/boundary';
import type { Token } from '../battlefield/tokens';
import type { Solid } from '../battlefield/types';
import { addParticipants, resetRound, sortParticipants, withSurprise } from '../turnTracker';
import { DEFAULT_INITIATIVE_ARRIVAL } from '../initiative';
import type { InitiativeTieBreak } from '../initiative';
import type { TurnParticipant, TurnTrackerState } from '../../types';
import type { Sides } from './sides';

type Placement = 'asTheyAre' | 'apart' | 'byHand';

type Surprised = 'nobody' | 'opposition' | 'party';

export interface FightPlan {
  placement: Placement;
  /** Metres between the two sides when they are placed apart. */
  distance: number;
  /** Roll initiative again for everyone, not only for those who have none. Not with the order kept. */
  rerollInitiative: boolean;
  /** Keep the order that is running (its round and whose turn it is) rather than restart it. */
  keepOrder: boolean;
  surprised: Surprised;
}

/** A short bowshot: close enough to matter in round one, far enough that nobody starts in melee. */
export const DEFAULT_FIGHT_DISTANCE = 20;

export const defaultFightPlan = (): FightPlan => ({
  placement: 'asTheyAre',
  distance: DEFAULT_FIGHT_DISTANCE,
  rerollInitiative: false,
  keepOrder: true,
  surprised: 'nobody',
});

/** Is there an order to keep: running, with somebody in it. */
export const orderRunning = (state: TurnTrackerState): boolean => state.active && state.participants.length > 0;

/**
 * The plan the dialog opens on for these sides: the default, with the
 * opposition surprised when the scene caught them unaware and there are two
 * sides for that to mean anything.
 */
export function openingPlan(sides: Sides): FightPlan {
  const plan = defaultFightPlan();
  const twoSides = sides.party.length > 0 && sides.opposition.length > 0;
  return sides.caughtUnaware && twoSides ? { ...plan, surprised: 'opposition' } : plan;
}

/** The combatant ids who sit out round one under this plan. */
export function sitsOutUnder(plan: Pick<FightPlan, 'surprised'>, sides: Sides): string[] {
  if (plan.surprised === 'nobody') return [];
  return sides[plan.surprised].map((c) => c.id);
}

interface Ground {
  boundary: Boundary;
  solids: Solid[];
}

/**
 * Where the two sides stand when placed apart: the party around a point
 * half the distance one way from the arena's centre, the opposition around
 * a point the same distance the other way, each a deployment in every
 * respect (clamped to the arena, stepped aside from walls and each other).
 * Returns the moved tokens only; someone with no token is not on the map
 * and is left out. Never mutates the tokens it was given.
 */
export function placeSidesApart(
  tokens: Readonly<Record<string, Token>>,
  sides: Sides,
  distance: number,
  { boundary, solids }: Ground,
): Token[] {
  const half = Math.max(0, distance) / 2;
  const { x, z } = boundary.centre;
  const around = (dx: number, id: string) => ({ id, ...clampToBoundary(boundary, x + dx, z), ring: defaultRing() });
  const standing = (side: Sides['party']) => side.map((c) => tokens[c.id]).filter((t): t is Token => !!t);
  const options = { avoidImpassable: true, avoidCombatants: true };
  const party = deploy(standing(sides.party), around(-half, 'fight-party'), boundary, solids, options);
  const opposition = deploy(standing(sides.opposition), around(half, 'fight-opposition'), boundary, solids, options);
  return [...Object.values(party.placed), ...Object.values(opposition.placed)];
}

interface OpenOptions {
  /** Throw the standing initiatives away and take the fresh ones for everybody. */
  reroll: boolean;
  sitsOut: readonly string[];
  tieBreak?: InitiativeTieBreak;
  /** Keep a running order (`orderRunning`): only newcomers are added. Ignored when nothing runs. */
  keep?: boolean;
}

/**
 * THE ORDER A FIGHT OPENS ON. `fresh` holds one participant per combatant
 * in the fight, initiative already rolled by the caller. Without a reroll,
 * whoever is already in the order keeps their place and their number, and
 * only the missing are added; with one, everybody takes their fresh roll.
 * Custom entries (no combatant) are the GM's own and always stay.
 *
 * Round 1, the first in order acting, a hand-made order respected, and the
 * surprised sitting out.
 */
export function openFightOrder(
  state: TurnTrackerState,
  fresh: readonly TurnParticipant[],
  { reroll, sitsOut, tieBreak, keep = false }: OpenOptions,
): TurnTrackerState {
  if (keep && orderRunning(state)) return keepFightOrder(state, fresh, sitsOut, tieBreak);
  const kept = state.participants.filter((p) => !reroll || p.combatantId === null);
  const known = new Set(kept.map((p) => p.combatantId));
  const participants = [...kept, ...fresh.filter((p) => !known.has(p.combatantId))];
  const opened: TurnTrackerState = { ...state, active: true, paused: false, joined: [], participants };
  const ordered = opened.orderMode === 'initiative' ? sortParticipants(opened, tieBreak) : opened;
  return withSurprise(resetRound(ordered), sitsOut);
}

/**
 * THE ORDER KEPT: everything as it runs - the round, whose turn it is, every place and number - with whoever is not in
 * it yet let in through the door (`addParticipants`, as a scene cut's travellers are), and the surprised sitting out
 * what is left of this round.
 */
function keepFightOrder(
  state: TurnTrackerState,
  fresh: readonly TurnParticipant[],
  sitsOut: readonly string[],
  tieBreak?: InitiativeTieBreak,
): TurnTrackerState {
  const known = new Set(state.participants.map((p) => p.combatantId));
  const newcomers = fresh.filter((p) => !known.has(p.combatantId));
  const joined = addParticipants({ ...state, paused: false }, newcomers, {
    arrival: DEFAULT_INITIATIVE_ARRIVAL,
    tieBreak,
  });
  // Nobody newly surprised leaves a surprise already running alone: `withSurprise` with nobody would clear it (the
  // review, 2026-10-28).
  return sitsOut.length ? withSurprise(joined, sitsOut) : joined;
}

const SURPRISE_LINES: Record<Exclude<Surprised, 'nobody'>, string> = {
  opposition: 'The opposition is surprised',
  party: 'The party is surprised',
};

/** One line for the log: the fight starts, and who sits out its first round. */
export function fightLine(plan: Pick<FightPlan, 'surprised'>, sides: Sides): string {
  if (plan.surprised === 'nobody') return 'The fight starts.';
  const names = sides[plan.surprised].map((c) => c.name);
  if (names.length === 0) return 'The fight starts.';
  const verb = names.length === 1 ? 'sits' : 'sit';
  return `The fight starts. ${SURPRISE_LINES[plan.surprised]}: ${names.join(', ')} ${verb} out round 1.`;
}
